
import React, { useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const ServicesPage = () => {
  useEffect(() => {
    // Update page title and meta description for services page
    document.title = 'IT Services - WOTECH Enterprise Solutions | ManageEngine, AutomMate, Zoho One';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Comprehensive IT services including ManageEngine implementation, AutomMate automation, Zoho One integration, ITSM consulting, and enterprise solutions.');
    }

    // Add structured data for Service page
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Enterprise IT Services",
      "description": "Comprehensive technology solutions including ITSM, ITOM, automation, and enterprise integrations.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`,
      "provider": {
        "@type": "Organization",
        "name": "WOTECH"
      },
      "serviceType": [
        "Software Licensing & Procurement",
        "Implementation & Configuration", 
        "Integration Services",
        "Business Process Automation",
        "Consulting & ITIL Process Design",
        "Training & Enablement"
      ],
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Services",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      icon: 'ri-settings-3-line',
      title: 'Software Licensing & Procurement',
      description: 'Official reseller and implementation partner for ManageEngine, AutomMate, and Zoho One solutions with comprehensive licensing support.',
      features: ['ManageEngine Suite', 'AutomMate Platform', 'Zoho One Ecosystem', 'Volume Licensing'],
      detailedDescription: 'As an official reseller and implementation partner, we provide comprehensive software licensing solutions that ensure your organization has access to the right tools at the right price. Our expertise spans across multiple platforms, enabling seamless procurement processes and ongoing license management.',
      benefits: [
        'Cost-effective licensing strategies',
        'Official vendor partnerships',
        'Compliance management',
        'License optimization consulting'
      ]
    },
    {
      icon: 'ri-tools-line',
      title: 'Implementation & Configuration',
      description: 'End-to-end setup and optimization of ITSM, ITOM, RPA, and integrated automation solutions tailored to your business needs.',
      features: ['ITSM Setup', 'ITOM Configuration', 'RPA Deployment', 'System Optimization'],
      detailedDescription: 'Our implementation services ensure that your technology investments deliver maximum value from day one. We handle everything from initial setup to complex configurations, ensuring systems are optimized for your specific business requirements.',
      benefits: [
        'Faster time to value',
        'Best practice configurations',
        'Minimal business disruption',
        'Comprehensive testing and validation'
      ]
    },
    {
      icon: 'ri-links-line',
      title: 'Integration Services',
      description: 'Connecting ManageEngine modules with enterprise tools such as Microsoft 365, SAP, HR, and CRM systems for seamless operations.',
      features: ['API Integration', 'Data Synchronization', 'Workflow Automation', 'System Connectivity'],
      detailedDescription: 'Breaking down silos between systems is crucial for operational efficiency. Our integration services connect your various platforms and applications, creating a unified ecosystem that enhances productivity and data accuracy.',
      benefits: [
        'Seamless data flow',
        'Reduced manual work',
        'Improved data accuracy',
        'Enhanced user experience'
      ]
    },
    {
      icon: 'ri-robot-line',
      title: 'Business Process Automation',
      description: 'Design and deployment of workflow automation using AutomMate to eliminate repetitive tasks and streamline operations.',
      features: ['RPA Solutions', 'Workflow Design', 'Task Automation', 'Process Optimization'],
      detailedDescription: 'Transform your business operations with intelligent automation solutions. We design and implement workflows that eliminate manual, repetitive tasks, allowing your team to focus on strategic initiatives that drive business growth.',
      benefits: [
        'Increased operational efficiency',
        'Reduced human error',
        'Cost savings through automation',
        'Scalable process improvements'
      ]
    },
    {
      icon: 'ri-user-settings-line',
      title: 'Consulting & ITIL Process Design',
      description: 'Advisory and implementation of ITIL-aligned service models, process maturity assessments, and service catalog design.',
      features: ['ITIL Consulting', 'Process Design', 'Service Catalog', 'Maturity Assessment'],
      detailedDescription: 'Align your IT operations with global best practices through our ITIL consulting services. We assess your current processes, identify improvement opportunities, and design service models that enhance efficiency and customer satisfaction.',
      benefits: [
        'ITIL framework alignment',
        'Process maturity improvements',
        'Service quality enhancement',
        'Compliance with industry standards'
      ]
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Training & Enablement',
      description: 'Structured learning programs covering ITIL fundamentals, ITSM best practices, automation workflows, and platform expertise.',
      features: ['ITIL Training', 'Platform Certification', 'Best Practices', 'Skill Development'],
      detailedDescription: 'Empower your team with comprehensive training programs designed to maximize the value of your technology investments. Our structured learning approach ensures sustainable adoption and continuous improvement.',
      benefits: [
        'Improved user adoption',
        'Enhanced platform utilization',
        'Certified expertise development',
        'Ongoing support and guidance'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">Our Core Services</h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Comprehensive technology solutions that bridge people, processes, and platforms to ensure improved productivity, operational efficiency, and compliance with global IT standards.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="p-8">
                  {/* Service Header */}
                  <div className="flex items-start mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-teal-100 rounded-xl flex items-center justify-center mr-6 flex-shrink-0">
                      <i className={`${service.icon} text-3xl text-blue-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{service.description}</p>
                    </div>
                  </div>

                  {/* Detailed Description */}
                  <div className="mb-6">
                    <p className="text-gray-700 leading-relaxed mb-4">{service.detailedDescription}</p>
                  </div>

                  {/* Core Features */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Core Features</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center">
                          <i className="ri-check-line text-teal-500 mr-2 text-sm"></i>
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Key Benefits */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Benefits</h4>
                    <ul className="space-y-2">
                      {service.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start">
                          <i className="ri-arrow-right-s-line text-blue-600 mr-2 mt-0.5 text-sm"></i>
                          <span className="text-sm text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Transform Your IT Operations?</h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Let's discuss how our comprehensive services can help optimize your technology infrastructure and drive business growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="http://wotech.io/#contact">
              <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                Get Started Today
              </button>
            </a>
            <a href="http://wotech.io/#about">
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Learn More About Us
            </button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
